import { apiFetch, downloadAuthenticatedFile } from "../api/client";
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { tenantPath } from "../utils/tenantContext";
function formatDate(value) {
  if (!value) return "N/A";

  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleString();
}

function formatLabel(value) {
  if (!value) return "Unknown";

  return String(value)
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function ReportsCenterPage() {
  const [report, setReport] = useState(null);
  const [archivedReports, setArchivedReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const loadReport = useCallback(async (silent = false) => {
    try {
      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      setError("");

      const [summaryResponse, archiveResponse] =
        await Promise.all([
          apiFetch(tenantPath(`/reporting/executive-summary`)),
          apiFetch(tenantPath(`/reporting/archive?status=generated&limit=100`)),
        ]);

      const summaryData = await summaryResponse.json();
      const archiveData = await archiveResponse.json();

      if (!summaryResponse.ok) {
        throw new Error(
          summaryData.detail || "Unable to load reporting data."
        );
      }

      if (!archiveResponse.ok) {
        throw new Error(
          archiveData.detail || "Unable to load generated reports."
        );
      }

      setReport(summaryData);
      setArchivedReports(
        Array.isArray(archiveData) ? archiveData : []
      );
    } catch (loadError) {
      console.error("Reports Center error:", loadError);
      setError(loadError.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  useEffect(() => {
    const handleTenantChange = () => {
      loadReport();
    };

    window.addEventListener(
      "wirelesspress-tenant-change",
      handleTenantChange
    );

    return () => {
      window.removeEventListener(
        "wirelesspress-tenant-change",
        handleTenantChange
      );
    };
  }, [loadReport]);

  const downloadExecutivePdf = async () => {
    try {
      setError("");

      await downloadAuthenticatedFile(
        tenantPath(`/reporting/executive-summary.pdf`),
        "wirelesspress-executive-summary.pdf"
      );
    } catch (downloadError) {
      setError(downloadError.message);
    }
  };

  const downloadIncidentCsv = async () => {
    try {
      setError("");

      await downloadAuthenticatedFile(
        tenantPath(`/reporting/incidents.csv`),
        "wirelesspress-incidents.csv"
      );
    } catch (downloadError) {
      setError(downloadError.message);
    }
  };

  const downloadArchivedReport = async (item) => {
    try {
      setError("");

      await downloadAuthenticatedFile(
        tenantPath(`/reporting/archive/${item.id}/download`),
        item.file_name || `wirelesspress-report-${item.id}.pdf`
      );
    } catch (downloadError) {
      setError(downloadError.message);
    }
  };

  const downloadIncidentData = async (incident) => {
    try {
      setError("");

      await downloadAuthenticatedFile(
        tenantPath(`/reporting/incidents/${incident.id}`),
        `incident-${incident.incident_number || incident.id}.json`
      );
    } catch (downloadError) {
      setError(downloadError.message);
    }
  };

  const downloadIncidentPdf = async (incident) => {
    try {
      setError("");

      await downloadAuthenticatedFile(
        tenantPath(`/reporting/incidents/${incident.id}.pdf`),
        `incident-${incident.incident_number || incident.id}.pdf`
      );
    } catch (downloadError) {
      setError(downloadError.message);
    }
  };

  if (loading) {
    return <div className="reports-loading">Loading reports...</div>;
  }

  const incidentMetrics = report?.incident_metrics || {};
  const edrMetrics = report?.edr_metrics || {};
  const soarMetrics = report?.soar_metrics || {};
  const threatMetrics = report?.threat_metrics || {};

  const recentIncidents = Array.isArray(report?.recent_incidents)
    ? report.recent_incidents
    : [];

  return (
    <div className="reports-page">
      <section className="reports-header">
        <div>
          <div className="reports-eyebrow">
            SECURITY OPERATIONS REPORTING
          </div>

          <h2>Reports & Executive Intelligence</h2>

          <p>
            Review SOC performance, generate incident reports,
            and export operational data.
          </p>
        </div>

        <div className="reports-header-actions">
          <button
            type="button"
            className="reports-executive-pdf-button"
            onClick={downloadExecutivePdf}
          >
            Download Executive PDF
          </button>

          <button type="button" onClick={downloadIncidentCsv}>
            Export Incidents CSV
          </button>

          <button
            type="button"
            onClick={() => loadReport(true)}
            disabled={refreshing}
          >
            {refreshing ? "Refreshing..." : "Refresh Reports"}
          </button>
        </div>
      </section>

      {error && (
        <div className="reports-warning">
          Latest refresh failed: {error}
        </div>
      )}

      <section className="reports-metric-grid">
        <article>
          <span>Total Incidents</span>
          <strong>{incidentMetrics.total_incidents || 0}</strong>
          <small>{incidentMetrics.open_incidents || 0} open</small>
        </article>

        <article className="reports-critical-card">
          <span>P1 Incidents</span>
          <strong>{incidentMetrics.p1_incidents || 0}</strong>
          <small>Critical cases</small>
        </article>

        <article className="reports-high-card">
          <span>P2 Incidents</span>
          <strong>{incidentMetrics.p2_incidents || 0}</strong>
          <small>High-priority cases</small>
        </article>

        <article>
          <span>Completed EDR Actions</span>
          <strong>{edrMetrics.completed_actions || 0}</strong>
          <small>{edrMetrics.failed_actions || 0} failed</small>
        </article>

        <article>
          <span>Completed SOAR Runs</span>
          <strong>{soarMetrics.completed_runs || 0}</strong>
          <small>{soarMetrics.failed_runs || 0} failed</small>
        </article>

        <article>
          <span>Active Indicators</span>
          <strong>{threatMetrics.active_indicators || 0}</strong>
          <small>
            {threatMetrics.critical_indicators || 0} critical
          </small>
        </article>
      </section>

      <section className="reports-panel">
        <div className="reports-panel-heading">
          <div>
            <span>SCHEDULED REPORT ARCHIVE</span>
            <h3>Generated Reports</h3>
          </div>

          <strong className="reports-archive-count">
            {archivedReports.length}
          </strong>
        </div>

        <div className="reports-table-wrapper">
          <table className="reports-table">
            <thead>
              <tr>
                <th>Report</th>
                <th>Type</th>
                <th>Period</th>
                <th>Size</th>
                <th>Generated</th>
                <th>Download</th>
              </tr>
            </thead>

            <tbody>
              {archivedReports.length > 0 ? (
                archivedReports.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <strong>{item.report_name}</strong>
                      <span className="reports-archive-file">
                        {item.file_name}
                      </span>
                    </td>

                    <td>{formatLabel(item.report_type)}</td>

                    <td>
                      <span className="reports-archive-period">
                        {formatDate(item.period_start)}
                      </span>
                      <span className="reports-archive-period">
                        to {formatDate(item.period_end)}
                      </span>
                    </td>

                    <td>
                      {item.file_size_bytes
                        ? `${Math.max(
                            1,
                            Math.round(item.file_size_bytes / 1024)
                          )} KB`
                        : "N/A"}
                    </td>

                    <td>{formatDate(item.created_at)}</td>

                    <td>
                      <button
                          type="button"
                          className="reports-archive-download"
                          onClick={() => downloadArchivedReport(item)}
                        >
                          Download PDF
                        </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="reports-empty">
                    No generated reports are available.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="reports-panel">
        <div className="reports-panel-heading">
          <div>
            <span>REPORT INVENTORY</span>
            <h3>Recent Incident Reports</h3>
          </div>
        </div>

        <div className="reports-table-wrapper">
          <table className="reports-table">
            <thead>
              <tr>
                <th>Incident</th>
                <th>Priority</th>
                <th>Source</th>
                <th>Status</th>
                <th>Created</th>
                <th>Reports</th>
              </tr>
            </thead>

            <tbody>
              {recentIncidents.length > 0 ? (
                recentIncidents.map((incident) => (
                  <tr key={incident.id}>
                    <td>
                      <Link
                        to={`/incidents/${incident.id}`}
                        className="reports-incident-link"
                      >
                        <strong>{incident.incident_number}</strong>
                        <span>{incident.title}</span>
                      </Link>
                    </td>

                    <td>{incident.priority || "P4"}</td>
                    <td>{formatLabel(incident.source)}</td>
                    <td>{formatLabel(incident.status)}</td>
                    <td>{formatDate(incident.created_at)}</td>

                    <td>
                      <div className="reports-row-actions">
                        <button
                            type="button"
                            onClick={() => downloadIncidentData(incident)}
                          >
                            Data
                          </button>

                        <button
                            type="button"
                            className="reports-pdf-link"
                            onClick={() => downloadIncidentPdf(incident)}
                          >
                            PDF
                          </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="reports-empty">
                    No incident reports available.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

export default ReportsCenterPage;
